<template>
  <div class="mpm-field-wrapper">
    <template v-if="$slots.prepend || prepend">
      <span class="mpm-field-prepend">
        <slot name="prepend">{{ prepend }}</slot>
      </span>
    </template>
    <div class="mpm-field-wrapper-inner" :style="{ width: getPxToRem(width) }">
      <van-field
        v-model="inputValue"
        v-bind="$attrs"
        v-on="$listeners"
        class="mpm-field"
        :class="[customClass, append && appendBg === '#f8f8f8' ? '' : 'border-radius-12']"
        :placeholder="placeholder"
        :formatter="formatter || limitFormatter"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      <template v-if="$slots.append || append">
        <div class="mpm-field-append">
          <slot name="append">
            <div class="mpm-pr16 mpm-field-append" :style="{ backgroundColor: appendBg }">
              {{ append }}
            </div>
          </slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MpmField',
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    formatter: {
      type: Function,
      default: undefined,
    },
    // 新增：格式化类型 int 或 decimal
    formatType: {
      type: String,
      default: 'int', // 默认整数
      validator: (val) => ['int', 'decimal'].includes(val),
    },
    width: {
      type: [String, Number],
      default: '100%',
    },
    prepend: {
      type: String,
      default: '',
    },
    append: {
      type: String,
      default: '',
    },
    appendBg: {
      type: String,
      default: '#f8f8f8',
    },
    min: {
      type: [String, Number],
      default: undefined,
    },
    max: {
      type: [String, Number],
      default: undefined,
    },
    placeholder: {
      type: String,
      default: '输入',
    },
  },
  data() {
    return {
      inputValue: this.value,
    }
  },
  watch: {
    value(newVal) {
      this.inputValue = newVal
    },
  },
  methods: {
    getPxToRem(width) {
      if ((typeof width === 'string' && width.endsWith('px')) || typeof width === 'number') {
        const pxValue = parseFloat(width)
        const remValue = pxValue / 100
        return `${remValue}rem`
      }
      return width
    },
    limitFormatter(value) {
      if (this.$attrs.type !== 'number') return value
      let newValue = value
      // 格式化类型处理
      if (this.formatType === 'decimal') {
        // 只允许数字和小数点
        newValue = newValue.toString().replace(/[^\d.]/g, '')
        // 只保留第一个小数点
        newValue = newValue.replace(/\.(?=.*\.)/g, '')
        // 限制只能输入两位小数
        newValue = newValue.replace(/^(\d+)(\.\d{0,2})?.*$/, '$1$2')
        // 处理前导0：如果是"0."开头，保留；否则去掉多余的0
        if (newValue.startsWith('0') && newValue.length > 1 && newValue[1] !== '.') {
          newValue = newValue.replace(/^0+(\d+)/, '$1')
        }
      } else {
        // int模式，只允许整数
        newValue = newValue.toString().replace(/\D/g, '')
        newValue = newValue.replace(/^0+(\d+)/, '$1')
      }
      return newValue
    },
    handleInput(value) {
      this.$emit('input', value)
      this.$emit('update:value', value)
    },
    handleChange(value) {
      this.$emit('change', value)
    },
    handleBlur(event) {
      let val = this.inputValue
      // 过滤前后空格
      if (typeof val === 'string') {
        val = val.trim()
      }

      if (this.$attrs.type === 'number') {
        if (this.formatType === 'decimal' && val !== '' && !isNaN(val)) {
          let num = Number(val)
          if (this.min !== undefined && num < Number(this.min)) {
            num = Number(this.min)
          }
          if (this.max !== undefined && num > Number(this.max)) {
            num = Number(this.max)
          }
          // 保留用户输入的小数位数
          val = num.toString()
        }
        if (this.formatType === 'int' && val !== '' && !isNaN(val)) {
          let num = Number(val)
          if (this.min !== undefined && num < Number(this.min)) {
            num = Number(this.min)
          }
          if (this.max !== undefined && num > Number(this.max)) {
            num = Number(this.max)
          }
          val = num.toString()
        }
      }
      this.$emit('input', val)
      this.$emit('update:value', val)
      this.$emit('blur', event)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    },
  },
}
</script>

<style lang="scss" scoped>
.mpm-field-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.mpm-field-wrapper-inner {
  display: flex;
  align-items: center;
  border-radius: 12px;
  overflow: hidden;

  :deep(.van-cell::after) {
    border-bottom: none;
  }

  .border-radius-12 {
    border-radius: 12px;
  }
}

.mpm-field-prepend,
.mpm-field-append {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.mpm-field-prepend {
  margin-right: 16px;
}

.mpm-field-append {
  height: 68px;
  color: #999999;
  font-size: 28px;
  font-weight: 400;
  overflow: hidden;
  flex-shrink: 0;
}

.van-field.mpm-field {
  flex: 1 1 auto;
  padding: 0 16px;
  background: #f8f8f8;
  min-height: 68px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  // border-radius: 12px;
  font-size: 28px;

  :deep(input::-webkit-input-placeholder) {
    color: #999999 !important;
    font-size: 28px !important;
  }

  :deep(input::-ms-input-placeholder) {
    color: #999999 !important;
    font-size: 28px !important;
  }

  :deep(input::-moz-placeholder) {
    color: #999999 !important;
    font-size: 28px !important;
  }
}
</style>
